import { availableLanguages } from '@sysarb/sysarb-types/general/locales';

export enum NumberFormats {
    baseCurrency = 'baseCurrency',
    integerSingleDigit = 'integerSingleDigit',
    integer = 'integer',
    decimal = 'decimal',
    decimalSingleFraction = 'decimalSingleFraction',
    decimalOptionalTwoDigitFraction = 'decimalOptionalTwoDigitFraction',
    decimalThreeDigit = 'decimalThreeDigit',
    decimalFourDigit = 'decimalFourDigit',
    percent = 'percent',
    fractionalPercent = 'fractionalPercent',
    fractionalPercentOneDigit = 'fractionalPercentOneDigit',
    fractionalPercentTwoDigit = 'fractionalPercentTwoDigit',
    fractionalPercentOptionalSingleDigit = 'fractionalPercentOptionalSingleDigit',
    fractionalPercentOptionalTwoDigit = 'fractionalPercentOptionalTwoDigit',
    roundedPercent = 'roundedPercent',
    years = 'years',
}

const years = {
    style: 'unit',
    unit: 'year',
    unitDisplay: 'short',
    maximumFractionDigits: 0,
    useGrouping: false
};

/** Currency Formats */
const baseCurrency = {
    style: 'currency',
    currencyDisplay: 'code',
    maximumFractionDigits: 2,
};

/** Integer Formats */
const integerSingleDigit = {
    minimumIntegerDigits: 1,
    maximumFractionDigits: 0,
    useGrouping: true
};
const integer = {
    minimumIntegerDigits: 2,
    maximumFractionDigits: 0,
    useGrouping: true
};

/** Decimal Formats */
const decimal = {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
};
const decimalSingleFraction = {
    style: 'decimal',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: true
};

const decimalOptionalTwoDigitFraction = {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true
};

const decimalOptionalSingleFraction = {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    useGrouping: true
};

const decimalThreeDigit = {
    style: 'decimal',
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
    useGrouping: false
};

const decimalFourDigit = {
    style: 'decimal',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
    useGrouping: false
};

/** Percent Formats */
const percent = {
    style: 'percent',
    maximumFractionDigits: 0,
};
const fractionalPercent = {
    style: 'percent',
    maximumFractionDigits: 1,
};
const fractionalPercentOneDigit = {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: true
};
const fractionalPercentTwoDigit = {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true
};

const fractionalPercentOptionalSingleDigit = {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    useGrouping: true
};

const fractionalPercentOptionalTwoDigit = {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true
};

const roundedPercent = {
    style: 'percent',
    maximumFractionDigits: 0,
    useGrouping: true,
    roundingPriority: 'auto'
};

const numberFormatDefault = {
    currency: {
        ...baseCurrency,
        currency: 'SEK'
    },
    integerSingleDigit,
    integer,
    decimal,
    decimalSingleFraction,
    decimalOptionalSingleFraction,
    decimalOptionalTwoDigitFraction,
    decimalThreeDigit,
    decimalFourDigit,
    percent,
    fractionalPercent,
    fractionalPercentOneDigit,
    fractionalPercentTwoDigit,
    fractionalPercentOptionalSingleDigit,
    fractionalPercentOptionalTwoDigit,
    roundedPercent,
    years,
};

const numberFormats: Record<string, unknown> = {
    en: {
        ...numberFormatDefault,
        currency: {
            ...baseCurrency,
            currency: 'USD',
            useGrouping: true
        },
        decimal: {
            ...decimal,
            useGrouping: true
        },
        percent: {
            ...percent,
            useGrouping: true
        },
        fractionalPercent: {
            ...fractionalPercent,
            useGrouping: true
        }
    },
    sv: {
        ...numberFormatDefault,
        currency: {
            ...baseCurrency,
            currency: 'SEK'
        },
    },
    nb: {
        ...numberFormatDefault,
        currency: {
            ...baseCurrency,
            currency: 'NOK',
        },
    }
};

export function getNumberFormats(): any {
    const result = { ...numberFormats };

    for (const lang of availableLanguages) {
        if (lang in result) continue;
        result[lang] = numberFormatDefault;
    }

    return result;
}
